import { Injectable, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import {
  ActivityWithCategoryAndLocationsAndReportedDetails,
  DemarcatorRange,
} from '@wilson/interfaces';
import { ActivityDragInformation } from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  DragAndDropModeService,
  TabEnum,
} from '@wilson/shift-timeline/services';
import { SetDemarcatorRange } from '@wilson/shift-timeline/state';
import { firstValueFrom } from 'rxjs';
import { ActivityDropZoneHelperService } from '../components/activity-drop-zone/activity-drop-zone-helper.service';

@Injectable()
export class GanttActivityDragHandlerHelperService {
  private readonly store = inject(Store);
  private readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly activityDropZoneHelperService = inject(
    ActivityDropZoneHelperService,
  );

  async startDrag(dragInformation: ActivityDragInformation): Promise<void> {
    this.dragAndDropModeService.setUnassignedRegionInUrl(
      TabEnum.UnassignedActivities,
    );
    const isAssignmentMode = await firstValueFrom(
      this.dragAndDropModeService.isAssignmentMode$,
    );
    const isMoveMode = await firstValueFrom(
      this.dragAndDropModeService.isMoveMode$,
    );
    setTimeout(() => {
      if (isAssignmentMode) {
        this.dragVertically(
          dragInformation.activity,
          dragInformation.leftPx,
          dragInformation.rightPx,
          dragInformation.userId,
        );
      }
      if (isMoveMode) {
        this.dragHorizontally(dragInformation.activity, dragInformation.userId);
      }
    });
  }

  setDemarcatorRange(demarcatorRange: DemarcatorRange[]): void {
    this.store.dispatch([new SetDemarcatorRange(demarcatorRange)]);
  }

  dragEnd(): void {
    this.store.dispatch(new SetDemarcatorRange([]));
    this.dragAndDropModeService.setActivityDropZone(false);
    this.dragAndDropModeService.setMoveActivityDropZone(false);
    this.dragAndDropModeService.setDraggingItemId(null);
    this.activityDropZoneHelperService.setUserId(null);
  }

  private dragVertically(
    activity: ActivityWithCategoryAndLocationsAndReportedDetails,
    leftPx: number | null,
    rightPx: number | null,
    userId: string | null,
  ): void {
    this.activityDropZoneHelperService.setActivity(activity);
    this.activityDropZoneHelperService.setUserId(userId);
    if (leftPx !== null && rightPx !== null) {
      this.store.dispatch([
        new SetDemarcatorRange([
          {
            left: leftPx,
            right: rightPx,
          },
        ]),
      ]);
      this.dragAndDropModeService.setActivityDropZone(true);
      if (activity.id) {
        this.dragAndDropModeService.setDraggingItemId(activity.id);
      }
    }
  }

  private dragHorizontally(
    activity: ActivityWithCategoryAndLocationsAndReportedDetails,
    userId: string | null,
  ): void {
    if (activity.id) {
      this.activityDropZoneHelperService.setActivity(activity);
      this.activityDropZoneHelperService.setUserId(userId);
      this.dragAndDropModeService.setMoveActivityDropZone(true);
      this.dragAndDropModeService.setDraggingItemId(activity.id);
    } else {
      console.error('Error: Dragging failed - Activity must have an ID.');
    }
  }
}
